/**
 * A stand-in for `node:zlib` so the PNG encoder runs in a browser.
 *
 * The browser does have deflate, via `CompressionStream`, but only
 * asynchronously, and `encodePng` is synchronous all the way down. Rather than
 * make the whole renderer async for one call, this emits **stored** deflate
 * blocks: valid zlib, no compression, a handful of lines, and synchronous.
 *
 * The cost is file size, which for a 32x32 sprite is a few hundred bytes and
 * nobody's problem. The thing to know is that a PNG downloaded from the browser
 * is **pixel-identical to the CLI's output but not byte-identical**, because the
 * CLI compresses properly. The CLI is the canonical render; the page is for
 * looking at and for getting something usable out quickly.
 */

const MAX_BLOCK = 0xffff;

function adler32(data: Uint8Array): number {
	let a = 1;
	let b = 0;
	for (let i = 0; i < data.length; i++) {
		a = (a + data[i]) % 65521;
		b = (b + a) % 65521;
	}
	return ((b << 16) | a) >>> 0;
}

export function deflateSync(data: Uint8Array, _opts?: unknown): Uint8Array {
	const blocks = Math.max(1, Math.ceil(data.length / MAX_BLOCK));
	const out = new Uint8Array(2 + blocks * 5 + data.length + 4);
	let at = 0;
	// zlib header: deflate, 32K window, no preset dictionary, check bits.
	out[at++] = 0x78;
	out[at++] = 0x01;
	for (let i = 0; i < blocks; i++) {
		const start = i * MAX_BLOCK;
		const len = Math.min(MAX_BLOCK, data.length - start);
		out[at++] = i === blocks - 1 ? 1 : 0; // final-block flag, stored type
		out[at++] = len & 0xff;
		out[at++] = (len >>> 8) & 0xff;
		out[at++] = ~len & 0xff;
		out[at++] = (~len >>> 8) & 0xff;
		out.set(data.subarray(start, start + len), at);
		at += len;
	}
	const sum = adler32(data);
	out[at++] = (sum >>> 24) & 0xff;
	out[at++] = (sum >>> 16) & 0xff;
	out[at++] = (sum >>> 8) & 0xff;
	out[at++] = sum & 0xff;
	return out.subarray(0, at);
}

/**
 * Reading a PNG is only needed to diff against art that already ships, which is
 * a CLI job. Failing loudly beats shipping a decoder the page never calls.
 */
export function inflateSync(_data: Uint8Array): Uint8Array {
	throw new Error("reading PNGs is not supported in the browser; use the CLI for match and sheet");
}
